// The bottom sheet's dismiss GESTURE, as a native scroll.
//
// The sheet lives inside a vertical CSS scroll-snap container with exactly two slots: a transparent
// full-viewport slot above it, and the panel itself, snapped to the end. At rest the scroller is parked on the
// panel; dragging the sheet down IS scrolling toward the empty slot, so the finger drag AND its momentum settle
// run entirely on the compositor thread — the browser's own fling physics, immune to whatever React is doing on
// the main thread. This is the same choice the route-transition engine's scroll-snap pager makes, and for the
// same reason: a scalar the compositor interpolates beats anything we can drive from a pointer handler, which is
// capped at the main thread's update rate (60Hz on a ProMotion iPhone whose panel runs at 120).
//
// What stays on the main thread is only the READ-BACK: a rAF-coalesced scroll handler publishes how far the
// sheet has travelled so the backdrop can fade with it, and the settle decides whether it landed on the empty
// slot (dismissed) or back on the panel. Starving that clock dims the backdrop a frame late; the sheet itself
// keeps moving.

import { useEffect, useLayoutEffect, type RefObject } from 'react'
// The sheet's dismiss and the nav stack's back-swipe are the SAME two-slot snap mechanic, so the read-back,
// the landed threshold and the settle debounce come from one place (see core/motion.config.ts).
// Relative, not aliased: this module is unit-tested and the pure-node runner resolves no `@/` alias
// (same constraint as lib/spring-travel.ts).
import { SNAP_LANDED, observeSnapScroller, snapProgress } from '../../lib/snap-scroller'

/** How far the panel has been dragged off, `0` (at rest) → `1` (fully dismissed). */
export const dismissProgress = snapProgress
/** How far onto the empty slot a settle must land to count as dismissed. */
export const DISMISS_LANDED = SNAP_LANDED

/**
 * How far the sheet has been dragged away, `0` (at rest, fully visible) → `1` (scrolled off). Derived from the
 * scroll position, which is the one thing both the compositor and we agree on.
 */

/** Nothing to do unless a settle actually landed on the empty slot: mid-flight, the browser is still snapping. */
export function settledDismissed(progress: number): boolean {
  return progress >= DISMISS_LANDED
}

interface SnapDismissArgs {
  /** The scroll-snap container: `fixed inset-0`, two slots, parked on the panel. */
  scrollerRef: RefObject<HTMLElement | null>
  /** Fades with the drag. Carries `--sheet-dismiss` and `data-scrolling` (which drops its transition to 0). */
  backdropRef: RefObject<HTMLElement | null>
  /** Called once a settle lands on the empty slot. The sheet is already off screen by then. */
  onDismiss: () => void
}

export function useSnapDismiss(args: SnapDismissArgs): void {
  const { scrollerRef, backdropRef, onDismiss } = args

  // Park on the panel BEFORE the first paint. A fresh scroller starts at scrollTop 0 — which is the dismissed
  // slot — so anything less than a pre-paint write would flash the sheet in its gone position.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    scroller.scrollTop = scroller.scrollHeight - scroller.clientHeight
  }, [scrollerRef])

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return

    const maxScroll = () => scroller.scrollHeight - scroller.clientHeight
    const progress = () => dismissProgress(scroller.scrollTop, maxScroll())
    /** Snap should have taken it home; a resize mid-gesture can leave it a pixel or two off, and a scroller
     *  parked off-rest never travels its full distance on the next drag. */
    const repark = () => {
      const rest = maxScroll()
      if (Math.abs(scroller.scrollTop - rest) > 1) scroller.scrollTop = rest
    }
    let scrolling = false

    const gesture = observeSnapScroller(scroller, {
      onStart: () => {
        scrolling = true
        // While the finger owns the sheet the backdrop must track it, not chase it through a 350ms transition.
        backdropRef.current?.setAttribute('data-scrolling', '')
      },
      onFrame: () => backdropRef.current?.style.setProperty('--sheet-dismiss', progress().toFixed(3)),
      onSettle: () => {
        scrolling = false
        backdropRef.current?.removeAttribute('data-scrolling')
        if (settledDismissed(progress())) {
          onDismiss()
          return
        }
        repark()
      },
    })

    // The panel's height changes the travel distance (a software keyboard, a row appearing after a mutation),
    // and the scroller has to stay parked on it. Never while a scroll is in flight — repositioning under a
    // fling eats the gesture.
    const observer = new ResizeObserver(() => {
      if (scrolling) return
      repark()
    })
    observer.observe(scroller)
    for (const child of scroller.children) observer.observe(child)

    return () => {
      gesture.stop()
      observer.disconnect()
    }
  }, [scrollerRef, backdropRef, onDismiss])
}
